import type { ActId } from "@/components/projects/diagnostic/acts";

/**
 * As notas do apresentador, ato a ato (#852).
 *
 * São a camada que o desenho aprovado acrescentou e que não existia em lugar
 * nenhum do produto: o que dizer, o que **não** abrir, e a resposta pronta para
 * a pergunta que o cliente costuma fazer naquele momento da conversa.
 *
 * Elas não são documentação: são parte da tela, condicionais a um toggle, e o
 * cabeçalho promete em voz alta que não entram no PDF do cliente.
 *
 * ⚠️ Essa promessa é uma dívida aberta, e está registrada na #852. O anexo de
 * hoje (#690) sai carimbado como uso interno e é um só; não existe versão de
 * cliente para estas notas ficarem de fora. Enquanto não existir, o que a tela
 * garante é que elas não aparecem para quem está olhando a projeção, e nada
 * mais.
 *
 * Os textos são transcrição do desenho. A chave é o inglês, que é a convenção do
 * i18n deste frontend.
 */
export interface PresenterNote {
  title: string;
  body: string;
}

const EFFECT = "Effect we're after";

export const PRESENTER_NOTES: Record<ActId, PresenterNote[]> = {
  environment: [
    {
      title: EFFECT,
      body: '"They know my landscape better than I do." No financial figures, no timeline. Named user lists and the Z object inventory stay out.',
    },
    {
      title: "If they ask for the full inventory",
      body: '"That full inventory is exactly the first deliverable of the project." Cite the sources (ST03N, SCMON, the ABAP repository, SU01), never the formula.',
    },
  ],
  diagnosis: [
    {
      title: EFFECT,
      body: "The client grasps the need and the size of the challenge. Counts and percentages yes; the list of the 43 gaps, no. No figures, no timelines, no effort in hours.",
    },
    {
      title: "If they ask how you classified it",
      body: '"By crossing your landscape data with the official SAP tables and our own project base." Sources, never the formula: the predicates of the decision engine are not explained.',
    },
  ],
  evidence: [
    {
      title: EFFECT,
      body: "Technical credibility plus expectation for the finale. Only these 3 samples; the other 40 points exist as a number, not as a list. Recommended path at the level of method, never an executable step by step.",
    },
    {
      title: "The hook",
      body: '"We even found a saving in your licensing. The number, I will show you shortly." Nobody leaves the room before Investment.',
    },
  ],
  treatment: [
    {
      title: EFFECT,
      body: "The final number arrives anchored in confidence. Show process and governance, never the engineering of the platform. Phase names always in the Exed methodology.",
    },
    {
      title: "If they ask for access to the platform",
      body: '"The platform is our operating desk. You receive the results and the governance of every decision, with full traceability."',
    },
  ],
  investment: [
    {
      title: "What to hold back in this act",
      body: "Explaining the metric of each SKU (what counts, the multipliers) is allowed and builds authority; the calculation sheet and the negotiated assumptions stay with Exed. No business case, no TCO, no comparisons: that goes in the proposal.",
    },
    {
      title: "Closing",
      body: '"These numbers came out of your landscape, not from a market table. The next step is formalising the proposal with this scope." If they ask for the price early: "it is in the last part, and it will make a lot more sense ten minutes from now".',
    },
  ],
};
